import React, { ReactNode } from 'react';
import styled from 'styled-components';
import { useIsMobile } from '../../hooks/useIsScreenSize';
import Font from '../../styles/font';

interface IStyledCardHeaderProps {
  $isDividerVisible?: boolean;
  $isStandalone?: boolean;
}

export const StyledCardHeader = styled.div<IStyledCardHeaderProps>`
  padding: 20px 0 ${props => (props.$isStandalone ? '20px' : 0)} 0;
  border-radius: ${props => (props.$isStandalone ? '12px' : '12px 12px 0 0')};
  background-color: ${props => props.theme.colors.surface.primary};

  ${props =>
    props.$isDividerVisible && !props.$isStandalone
      ? `border-bottom: 1px solid ${props.theme.colors.general.border}; padding-bottom: 16px;`
      : ''}

  .header-top {
    display: flex;
    align-items: center;

    & > *:not(:first-child) {
      margin: 0 0 0 16px;
    }

    .right {
      margin: 0 0 0 auto;
    }
  }

  .sub-title {
    margin-top: 8px;
    color: ${props => props.theme.colors.text.secondary};
  }
`;

export type HeaderTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

export interface ICardHeaderProps {
  title: string;
  subTitle?: string;
  as?: HeaderTag;
  rightAction?: ReactNode;
  leftAction?: ReactNode;
  badge?: ReactNode;
  isDividerVisible?: boolean;
  isStandalone?: boolean;
  id?: string;
}

const FONT_SIZE_MAP = new Map<HeaderTag, [Font, Font]>([
  ['h1', [Font.f20_heavy, Font.f28_heavy]],
  ['h2', [Font.f18_bold, Font.f20_bold]],
  ['h3', [Font.f16_bold_tall, Font.f18_bold]]
]);

const CardHeader: React.FC<ICardHeaderProps> = ({
  title,
  subTitle,
  as = 'h2',
  badge,
  rightAction,
  leftAction,
  isDividerVisible = true,
  isStandalone = false,
  ...rest
}) => {
  const isMobile = useIsMobile();
  const [mobileFont, desktopFont] = FONT_SIZE_MAP.get(as) as [Font, Font];
  const fontSize = isMobile ? mobileFont : desktopFont;

  const HTag = as;

  return (
    <StyledCardHeader
      $isDividerVisible={isDividerVisible}
      $isStandalone={isStandalone}
    >
      <div className="header-top card-padding-x">
        {leftAction && <div className="left">{leftAction}</div>}
        {title && (
          <HTag className={`${fontSize} header-text`} {...rest}>
            {title}
          </HTag>
        )}
        {badge}
        {rightAction && <div className="right">{rightAction}</div>}
      </div>
      {subTitle && (
        <div className={`sub-title card-padding-x ${Font.f12_tall}`}>
          {subTitle}
        </div>
      )}
    </StyledCardHeader>
  );
};

export default CardHeader;
